import React, { memo, useCallback } from 'react';

import Form from 'react-bootstrap/Form';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _truncate from 'lodash/truncate';

import SearchArticle from '../../common/SearchArticle';
import i18n from '../messages.json';
import { validURL } from '../../../utils/url-helper';

const ARTICLE_TYPE = 'Article';

/**
 * Renders one of two field variants depending on the active link type:
 * - Article: an inline preview card (image + headline + byline + truncated excerpt + Change button)
 *   when an article is selected; otherwise the SearchArticle typeahead.
 * - DirectURL: a single text input with URL validation.
 *
 * Props
 *   typeNotification:  'Article' | 'DirectURL'
 *   article:           currentMessage.article (or null)
 *   directUrl:         currentMessage.directUrl.value
 *   isArticleValid:    boolean — drives required-field invalid feedback
 *   onArticleChange:   (article|null) => void — also used to clear via Change button
 *   onDirectUrlChange: (event) => void
 */
const ArticleOrUrlField = ({
  typeNotification,
  article,
  directUrl,
  isArticleValid,
  onArticleChange,
  onDirectUrlChange,
}) => {
  const handleChangeArticle = useCallback(
    () => onArticleChange(null),
    [onArticleChange]
  );

  if (typeNotification !== ARTICLE_TYPE) {
    return (
      <Form.Group controlId='formDirectUrl'>
        <Form.Control
          value={directUrl}
          onChange={onDirectUrlChange}
          placeholder={i18n.ink.notification.directUrl.placeholder}
          isInvalid={validURL(directUrl) === false}
        />
        <Form.Control.Feedback type='invalid'>
          {i18n.ink.notification.directUrl.invalidUrl}
        </Form.Control.Feedback>
      </Form.Group>
    );
  }

  return (
    <Form.Group controlId='formArticle'>
      {article ? (
        <div className='article-card'>
          {!_isEmpty(_get(article, 'image_url')) && (
            <img
              className='article-card__image'
              src={article.image_url}
              alt=''
            />
          )}
          <div className='article-card__body'>
            <div className='article-card__title'>
              {_get(article, 'story_title')}
            </div>
            {!_isEmpty(_get(article, 'author')) && (
              <div className='article-card__byline'>
                by {article.author}
              </div>
            )}
            {!_isEmpty(_get(article, 'desc')) && (
              <div className='article-card__excerpt'>
                {_truncate(article.desc, { length: 140 })}
              </div>
            )}
          </div>
          <button
            type='button'
            className='article-card__change'
            onClick={handleChangeArticle}
            aria-label='Change article'
          >
            Change
          </button>
        </div>
      ) : (
        <SearchArticle
          updateSelected={onArticleChange}
          isValid={isArticleValid}
        />
      )}
      {isArticleValid === false && (
        <div className='invalid-feedback d-block'>
          {i18n.ink.notification.article.required}
        </div>
      )}
    </Form.Group>
  );
};

export default memo(ArticleOrUrlField);
